import { env } from "$env/dynamic/public"
import type {
  Address,
  AdminRpc,
  AdminRpcHandler,
  AppleWalletSettings,
  GoogleWalletSettings,
  PublicRpcHandler,
  TicketData,
  TicketToken
} from "@percs/api-types"
import { ApiError } from "../helpers/api.js"

let id = 0

// API Methods

export async function callApiRpc(method: string, params: unknown, { apiKey, apiUrl, fetch, signal }: { apiKey?: string | null, apiUrl: string, fetch: any, signal?: AbortSignal }) {
  const appHost = env.PUBLIC_APP_HOST
  if (!appHost) {
    throw new ApiError("Missing PUBLIC_APP_HOST.")
  }

  const response = await fetch(`${apiUrl}/rpc`, {
    method: "POST",
    credentials: "omit",
    ...(signal ? { signal } : {}),
    headers: {
      "Content-Type": "application/json",
      "Origin": appHost,
      ...(apiKey ? { "X-Api-Key": apiKey } : {}),
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      method,
      params,
      id: ++id,
    }),
  })

  const responseJson = await response.json()
  if (responseJson.error) {
    throw new ApiError(responseJson.error.message, responseJson.error.data)
  }
  return responseJson.result
}

interface ApiOptions {
  apiUrl: string
  fetch: Function
  signal?: AbortSignal
}

/** @todo import from api */
interface GetOfferParams {
  id: string
  slug: string
}

/**
 * @todo import from api
 * @todo think through rich text use case
 * @todo what other social are needed?
 * @todo what's the difference with title and name?
 * @todo should have other image options
 */
export interface Offer {
  id: string
  /** The slug for the canonical URL */
  slug: string
  /** Used in list views for the product */
  name: string
  /** The type of the offer.  Effects required fields and metadata */
  type: "event"
  /** The range of dates that this offer is valid for */
  validDates: [ start: Date | null, end: Date | null ]
  enabled: boolean
  /** Main image displayed on the offer page and socials */
  image: {
    src: string
    height?: number
    width?: number
    alt?: string
  }
  /**  */
  messaging: {
    /** Displayed in the hero section on the offer page & socials */
    title: string
    /** Displayed below title on offer page to guide users */
    instructions: string
    /** Used as the description on social shares */
    description: string
    /** Text for the CTA to redeem a token */
    redeemText: string
    /** Message displayed when you don't have the appropriate tokens */
    rejectText: string
  }
  /** The redemption parameters for this offer */
  redemption: {
    selectType: "single" | "multiple" | "all" | "wallet"
    // @todo should this include the redemption info?  qr_code, etc
  }
  template?: any

  appleWallet: boolean
}

/**
 * @todo import from api
 * @todo think through rich text use case
 * @todo what other social are needed?
 * @todo what's the difference with title and name?
 * @todo should have other image options
 */
export interface CreatorOffer {
  id: string
  /** The slug for the canonical URL */
  slug: string
  /** Used in list views for the product */
  name: string
  /** The type of the offer.  Effects required fields and metadata */
  type: "event"
  /** The range of dates that this offer is valid for */
  validDates: [ start: Date | null, end: Date | null ]
  enabled: boolean
  /** Main image displayed on the offer page and socials */
  image: {
    src: string
    height?: number
    width?: number
    alt?: string
  }
  /**  */
  messaging: {
    /** Displayed in the hero section on the offer page & socials */
    title: string
    /** Displayed below title on offer page to guide users */
    instructions: string
    /** Used as the description on social shares */
    description: string
    /** Text for the CTA to redeem a token */
    redeemText: string
    /** Message displayed when you don't have the appropriate tokens */
    rejectText: string
  }
  /** The redemption parameters for this offer */
  redemption: {
    selectType: "single" | "multiple" | "all"
    // @todo should this include the redemption info?  qr_code, etc
    auth?: string
    type: "qr_code"
    url?: string
    internal: boolean
    totalLimit: number | null
  }
}

export interface RedemptionList {
  csvFile: File
}

export async function getOffer(params: GetOfferParams, options: ApiOptions): Promise<Offer | null> {
  return callApiRpc("getOffer", params, options)
}

export interface GetOfferTicketParams {
  id: string
  slug: string
  value: string
}

export interface GetOfferTicketEventResponse {
  event: {
    name: string
    startDate?: {
      text: string
      value: string
    }
    expiryDate?: {
      text: string
      value: string
    }
    showTimes?: boolean
    date?: {
      startTime?: {
        text: string
      }
      endTime?: {
        text: string
      }
      text: string
    }
    address?: Address
    token?: TicketToken
    backgroundColor?: string
    traits?: Array<{ type: string, value: string }>
    appleWallet: AppleWalletSettings
    googleWallet: GoogleWalletSettings
  }
  apiUrl: string
}

export async function getOfferTicket(params: GetOfferTicketParams, options: ApiOptions): Promise<GetOfferTicketEventResponse | null> {
  return callApiRpc("getOfferTicket", params, options)
}

export interface EmailOfferTicketParams {
  ticketData: TicketData
  values?: Record<string, any>
}

export interface EmailOfferTicketResponse {
  result: true
}

export async function emailOfferTicket(params: EmailOfferTicketParams, options: ApiOptions): Promise<EmailOfferTicketResponse | null> {
  return callApiRpc("emailOfferTicket", params, options)
}

/** @todo import from api */
interface GetOwnedContractTokensParams {
  offerId: string
  accounts: string[]
}

/** @todo import from api */
type GetOwnedContractTokensResponse = Array<{
  availableRedemptionsCount?: number
  type: "ETH" | "custom"
  address: string
  name: string
  tokens: Record<
    string,
    {
      account: string
      imageThumbnailUrl?: string
      name: string
      isRedeemed: boolean
    }
  >
  redemptionFields?: {
    schema: {
      type: "object"
      properties: Record<
        string,
        {
          type: "string" | "email"
          label: string
          minLength?: number
          description?: string
        }
      >
    }
  }
}>

export async function getOwnedContractTokens(params: GetOwnedContractTokensParams, options: ApiOptions): Promise<GetOwnedContractTokensResponse> {
  return callApiRpc("getOwnedContractTokens", params, options)
}

/** @todo import from api */
interface RedeemTokensParams {
  offerId: string
  itemId?: string
  contractTokens: Array<{
    type: string
    address: string
    tokenIds: string[]
  }>,
  message: string
  signature: string
  values?: Record<string, any>
}

/** @todo import from api */
interface RedeemTokensResponse {
  contractTokenRedemptions: Array<{
    type: string
    address: string
    tokenRedemptions: Record<string, any>
  }>
}

export async function redeemTokens(params: RedeemTokensParams, options: ApiOptions): Promise<RedeemTokensResponse> {
  return callApiRpc("redeemTokens", params, options)
}

interface ValidateRedemptionParams {
  offerId: number | string
  value: string
  auth: string
  markUsed: boolean
}

export async function validateRedemption(params: ValidateRedemptionParams, options: ApiOptions) {
  return callApiRpc("validateRedemption", params, options)
}

export const getWalletChallenge: PublicRpcHandler<"getWalletChallenge"> = (params, options: ApiOptions) => callApiRpc("getWalletChallenge", params, options)
export const createAnonymousFrame: PublicRpcHandler<"createAnonymousFrame"> = (params, options: ApiOptions) => callApiRpc("createAnonymousFrame", params, options)
export const searchFarcasterUsersAndChannels: PublicRpcHandler<"searchFarcasterUsersAndChannels"> = (params, options: ApiOptions) => callApiRpc("searchFarcasterUsersAndChannels", params, options)
export const submitWalletChallengeVerifcation: PublicRpcHandler<"submitWalletChallengeVerifcation"> = (params, options: ApiOptions) => callApiRpc("submitWalletChallengeVerifcation", params, options)

// Local Methods

async function callCreatorRpc<T extends keyof AdminRpc>(method: T, params: AdminRpc[T]["params"] = {}, { fetch, signal = null }) {
  const response = await fetch("/creator/rpc", {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      method,
      params,
      id: ++id,
    }),
  })

  switch (response.status) {
    case 504: {
      throw new Error("RPC error: Service is temporarily unavailable, please try again shortly")
    }
    case 500:
    default: {
      const responseText = await response.text()
      try {
        const responseJson = JSON.parse(responseText)
        if (responseJson.error) {
          if (responseJson.error.data) {
            throw new ApiError(responseJson.error.message, responseJson.error.data)
          }
          throw new ApiError(responseJson.error.message)
        }
        return responseJson.result
      } catch (err) {
        console.error(err)
        throw err
      }
    }
  }
}

interface LocalOptions {
  fetch: Function
}

// Authentication

export const loginWithEmail: AdminRpcHandler<"loginWithEmail"> = (params, options: LocalOptions) =>
  callCreatorRpc("loginWithEmail", params, options)

export const loginWithWarpcast: AdminRpcHandler<"loginWithWarpcast"> = (params, options: LocalOptions) =>
  callCreatorRpc("loginWithWarpcast", params, options)

export const checkWarpcastLogin: AdminRpcHandler<"checkWarpcastLogin"> = (params, options: LocalOptions) =>
  callCreatorRpc("checkWarpcastLogin", params, options)

export const authenticate: AdminRpcHandler<"authenticate"> = (params, options: LocalOptions) =>
  callCreatorRpc("authenticate", params, options)

export const getAccounts: AdminRpcHandler<"getAccounts"> = (params, options: LocalOptions) =>
  callCreatorRpc("getAccounts", params, options)

export const updateAccount: AdminRpcHandler<"updateAccount"> = (params, options: LocalOptions) =>
  callCreatorRpc("updateAccount", params, options)

export const addAccountTeamMember: AdminRpcHandler<"addAccountTeamMember"> = (params, options: ApiOptions) =>
  callCreatorRpc("addAccountTeamMember", params, options)

export const removeAccountTeamMember: AdminRpcHandler<"removeAccountTeamMember"> = (params, options: ApiOptions) =>
  callCreatorRpc("removeAccountTeamMember", params, options)

export const getAccountNotifications: AdminRpcHandler<"getAccountNotifications"> = (params, options: LocalOptions) =>
  callCreatorRpc("getAccountNotifications", params, options)

export const dismissAccountNotification: AdminRpcHandler<"dismissAccountNotification"> = (params, options: LocalOptions) =>
  callCreatorRpc("dismissAccountNotification", params, options)

// Profile management

export const getProfileInfo: AdminRpcHandler<"getProfileInfo"> = (params, options: LocalOptions) =>
  callCreatorRpc("getProfileInfo", params, options)

export const updateProfile: AdminRpcHandler<"updateProfile"> = (params, options: LocalOptions) =>
  callCreatorRpc("updateProfile", params, options)

// Integrations

export const getApiKeys: AdminRpcHandler<"getApiKeys"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getApiKeys", params, options)

export const generateApiKey: AdminRpcHandler<"generateApiKey"> = async (params, options: ApiOptions) =>
  callCreatorRpc("generateApiKey", params, options)

export const updateApiKey: AdminRpcHandler<"updateApiKey"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateApiKey", params, options)

export const removeApiKey: AdminRpcHandler<"removeApiKey"> = async (params, options: ApiOptions) =>
  callCreatorRpc("removeApiKey", params, options)

export const getSalesforceCommerceCloudSites: AdminRpcHandler<"getSalesforceCommerceCloudSites"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getSalesforceCommerceCloudSites", params, options)

export const createIntegrationSetting: AdminRpcHandler<"createIntegrationSetting"> = async (params, options: ApiOptions) =>
  callCreatorRpc("createIntegrationSetting", params, options)

export const updateIntegrationSetting: AdminRpcHandler<"updateIntegrationSetting"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateIntegrationSetting", params, options)

export const getApps: AdminRpcHandler<"getApps"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getApps", params, options)

export const getIntegrations: AdminRpcHandler<"getIntegrations"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getIntegrations", params, options)

export const checkIntegration: AdminRpcHandler<"checkIntegration"> = async (params, options: ApiOptions) =>
  callCreatorRpc("checkIntegration", params, options)

// Offer Management

export const postCreatorOffer: AdminRpcHandler<"postCreatorOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorOffer", params, options)

export const getCreatorOffer: AdminRpcHandler<"getCreatorOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorOffer", params, options)

export const getCreatorOffers: AdminRpcHandler<"getCreatorOffers"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorOffers", params, options)

export const updateCreatorOffer: AdminRpcHandler<"updateCreatorOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorOffer", params, options)

export const enableCreatorOffer: AdminRpcHandler<"enableCreatorOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("enableCreatorOffer", params, options)

export const disableCreatorOffer: AdminRpcHandler<"disableCreatorOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("disableCreatorOffer", params, options)

export const exportRedemptionList: AdminRpcHandler<"exportRedemptionList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("exportRedemptionList", params, options)

export const generateOfferTickets: AdminRpcHandler<"generateOfferTickets"> = async (params, options: ApiOptions) =>
  callCreatorRpc("generateOfferTickets", params, options)

export const exportFrameAnalytics: AdminRpcHandler<"exportFrameAnalytics"> = async (params, options: ApiOptions) =>
  callCreatorRpc("exportFrameAnalytics", params, options)

// Customer offer data

export const generateOfferTokenData: AdminRpcHandler<"generateOfferTokenData"> = async (params, options: ApiOptions) =>
  callCreatorRpc("generateOfferTokenData", params, options)

// Audience Management

export const checkContract: AdminRpcHandler<"checkContract"> = async (params, options: ApiOptions) =>
  callCreatorRpc("checkContract", params, options)

export const getDraftOffer: AdminRpcHandler<"getDraftOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getDraftOffer", params, options)

export const getDraftOffers: AdminRpcHandler<"getDraftOffers"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getDraftOffers", params, options)

export const postDraftOffer: AdminRpcHandler<"postDraftOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postDraftOffer", params, options)

export const discardUnpublishedChanges: AdminRpcHandler<"discardUnpublishedChanges"> = async (params, options: ApiOptions) =>
  callCreatorRpc("discardUnpublishedChanges", params, options)

export const publishDraftOffer: AdminRpcHandler<"publishDraftOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("publishDraftOffer", params, options)

export const updateDraftOffer: AdminRpcHandler<"updateDraftOffer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateDraftOffer", params, options)

export const postCreatorCustomer: AdminRpcHandler<"postCreatorCustomer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorCustomer", params, options)

export const manageCustomersInCustomerLists: AdminRpcHandler<"manageCustomersInCustomerLists"> = async (params, options: ApiOptions) =>
  callCreatorRpc("manageCustomersInCustomerLists", params, options)

export const updateCustomerTags: AdminRpcHandler<"updateCustomerTags"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCustomerTags", params, options)

export const postCreatorAudience: AdminRpcHandler<"postCreatorAudience"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorAudience", params, options)

export const archiveCreatorAudience: AdminRpcHandler<"archiveCreatorAudience"> = async (params, options: ApiOptions) =>
  callCreatorRpc("archiveCreatorAudience", params, options)

export const refreshCreatorAudience: AdminRpcHandler<"refreshCreatorAudience"> = async (params, options: ApiOptions) =>
  callCreatorRpc("refreshCreatorAudience", params, options)

export const getCreatorAudiences: AdminRpcHandler<"getCreatorAudiences"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorAudiences", params, options)

export const getCreatorAudience: AdminRpcHandler<"getCreatorAudience"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorAudience", params, options)

export const updateCreatorAudience: AdminRpcHandler<"updateCreatorAudience"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorAudience", params, options)

export const getCreatorContract: AdminRpcHandler<"getCreatorContract"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorContract", params, options)

export const updateCreatorContract: AdminRpcHandler<"updateCreatorContract"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorContract", params, options)

export const updateCreatorContractOwners: AdminRpcHandler<"updateCreatorContractOwners"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorContractOwners", params, options)

export const updateCreatorContractToken: AdminRpcHandler<"updateCreatorContractToken"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorContractToken", params, options)

// Customers

export const getCreatorCustomers: AdminRpcHandler<"getCreatorCustomers"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorCustomers", params, options)

export const getCreatorTags: AdminRpcHandler<"getCreatorTags"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorTags", params, options)

export const getCreatorCustomer: AdminRpcHandler<"getCreatorCustomer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorCustomer", params, options)

export const getCreatorCustomerLists: AdminRpcHandler<"getCreatorCustomerLists"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorCustomerLists", params, options)

export const getCreatorFavoriteContactLists: AdminRpcHandler<"getCreatorFavoriteContactLists"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorFavoriteContactLists", params, options)

export const getCreatorContactLists: AdminRpcHandler<"getCreatorContactLists"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorContactLists", params, options)

export const getCreatorContactListsFull: AdminRpcHandler<"getCreatorContactListsFull"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorContactListsFull", params, options)

export const exportSubscriberList: AdminRpcHandler<"exportSubscriberList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("exportSubscriberList", params, options)

export const exportAudienceList: AdminRpcHandler<"exportAudienceList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("exportAudienceList", params, options)

export const updateCreatorCustomer: AdminRpcHandler<"updateCreatorCustomer"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorCustomer", params, options)

export const getCreatorContactList: AdminRpcHandler<"getCreatorContactList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorContactList", params, options)

export const postCreatorContactList: AdminRpcHandler<"postCreatorContactList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorContactList", params, options)

export const putCreatorContactList: AdminRpcHandler<"putCreatorContactList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("putCreatorContactList", params, options)

export const getCreatorContactStats: AdminRpcHandler<"getCreatorContactStats"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorContactStats", params, options)

// Messaging

export const saveCoinbasePartnerApiKey: AdminRpcHandler<"saveCoinbasePartnerApiKey"> = async (params, options: ApiOptions) =>
  callCreatorRpc("saveCoinbasePartnerApiKey", params, options)

export const createCustodialWallet: AdminRpcHandler<"createCustodialWallet"> = async (params, options: ApiOptions) =>
  callCreatorRpc("createCustodialWallet", params, options)

export const getCreatorMessages: AdminRpcHandler<"getCreatorMessages"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorMessages", params, options)

export const postCreatorMessage: AdminRpcHandler<"postCreatorMessage"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorMessage", params, options)

export const updateCreatorMessage: AdminRpcHandler<"updateCreatorMessage"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorMessage", params, options)

export const deleteCreatorMessage: AdminRpcHandler<"deleteCreatorMessage"> = async (params, options: ApiOptions) =>
  callCreatorRpc("deleteCreatorMessage", params, options)

export const sendMessageDeliveryList: AdminRpcHandler<"sendMessageDeliveryList"> = async (params, options: ApiOptions) =>
  callCreatorRpc("sendMessageDeliveryList", params, options)

export const sendTestMessage: AdminRpcHandler<"sendTestMessage"> = async (params, options: ApiOptions) =>
  callCreatorRpc("sendTestMessage", params, options)

// Frames
export const postCreatorFrame: AdminRpcHandler<"postCreatorFrame"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorFrame", params, options)

export const addFrameToAccount: AdminRpcHandler<"addFrameToAccount"> = async (params, options: ApiOptions) =>
  callCreatorRpc("addFrameToAccount", params, options)

export const duplicateCreatorFrame: AdminRpcHandler<"duplicateCreatorFrame"> = async (params, options: ApiOptions) =>
  callCreatorRpc("duplicateCreatorFrame", params, options)

export const updateCreatorFrame: AdminRpcHandler<"updateCreatorFrame"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorFrame", params, options)

export const setCreatorFramePublished: AdminRpcHandler<"setCreatorFramePublished"> = async (params, options: ApiOptions) =>
  callCreatorRpc("setCreatorFramePublished", params, options)

export const setCreatorFrameArchived: AdminRpcHandler<"setCreatorFrameArchived"> = async (params, options: ApiOptions) =>
  callCreatorRpc("setCreatorFrameArchived", params, options)

export const getCreatorFrames: AdminRpcHandler<"getCreatorFrames"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorFrames", params, options)

export const getCreatorFrame: AdminRpcHandler<"getCreatorFrame"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorFrame", params, options)

export const getCastImage: AdminRpcHandler<"getCastImage"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCastImage", params, options)

// Wallet Challenge Management

export const createWalletChallenge: AdminRpcHandler<"createWalletChallenge"> = async (params, options: ApiOptions) =>
  callCreatorRpc("createWalletChallenge", params, options)

export const getWalletChallenges: AdminRpcHandler<"getWalletChallenges"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getWalletChallenges", params, options)

// File Upload Management

export const uploadFile: AdminRpcHandler<"uploadFile"> = async (params, options: ApiOptions) =>
  callCreatorRpc("uploadFile", params, options)

export const uploadXmtpFile: AdminRpcHandler<"uploadXmtpFile"> = async (params, options: ApiOptions) =>
  callCreatorRpc("uploadXmtpFile", params, options)

// Admin

export const getAdminAccounts: AdminRpcHandler<"getAdminAccounts"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getAdminAccounts", params, options)

export const sendDailyAnalyticsEmail: AdminRpcHandler<"sendDailyAnalyticsEmail"> = async (params, options: ApiOptions) =>
  callCreatorRpc("sendDailyAnalyticsEmail", params, options)

export const sendDailyWorkerAuditEmail: AdminRpcHandler<"sendDailyWorkerAuditEmail"> = async (params, options: ApiOptions) =>
  callCreatorRpc("sendDailyWorkerAuditEmail", params, options)

export const runDailyAudienceStatsAudit: AdminRpcHandler<"runDailyAudienceStatsAudit"> = async (params, options: ApiOptions) =>
  callCreatorRpc("runDailyAudienceStatsAudit", params, options)

// Ecommerce Shopify Search Products

export const getEcommerceShopifyProducts: AdminRpcHandler<"getAdminEcommerceShopifyProducts"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getAdminEcommerceShopifyProducts", params, options)

export const getEcommerceShopifyProductImage: AdminRpcHandler<"getEcommerceShopifyProductImage"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getEcommerceShopifyProductImage", params, options)

export const exportFrameShopifyAnalytics: AdminRpcHandler<"exportFrameShopifyAnalytics"> = async (params, options: ApiOptions) =>
  callCreatorRpc("exportFrameShopifyAnalytics", params, options)

// Automation

export const postCreatorAutomation: AdminRpcHandler<"postCreatorAutomation"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorAutomation", params, options)

export const getCreatorAutomation: AdminRpcHandler<"getCreatorAutomation"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getCreatorAutomation", params, options)

export const updateCreatorAutomation: AdminRpcHandler<"updateCreatorAutomation"> = async (params, options: ApiOptions) =>
  callCreatorRpc("updateCreatorAutomation", params, options)

// onboarding

export const getOnboardingInfo: AdminRpcHandler<"getOnboardingInfo"> = async (params, options: ApiOptions) =>
  callCreatorRpc("getOnboardingInfo", params, options)

export const postCreatorOnboardingFlow: AdminRpcHandler<"postCreatorOnboardingFlow"> = async (params, options: ApiOptions) =>
  callCreatorRpc("postCreatorOnboardingFlow", params, options)

